import React, { Suspense, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Text from 'shared/components/Text/Text';
import Button from 'shared/components/Button/Button';
import { LoadingSpinner } from 'shared/components/LoadingSpinner';
import { CloseIcon } from 'shared/components/icons/CloseIcon';
import { useTheme } from 'styled-components';
import { useBreakpoints } from 'hooks/useWindowSize';
import { guessDeviceType, DeviceType } from 'shared/utils/guessDeviceType';
import { LINKS } from 'shared/links';
import { handleFocusedKeyPress } from 'shared/utils/handleFocusedKeypress';
import { perSessionBannersKey, perSessionMyEverBrightBannerKey } from 'constants/localStorageKeys';
import { isMobile } from 'react-device-detect';
import {
  BannerLeftContent,
  BannerTextContent,
  BannerWrapper,
  ButtonStyledWrapper,
  CloseBannerButton,
} from './MyEverBrightBanner.styles';

const AppGraphic = React.lazy(
  () => import('modules/status/components/Onboarding/MyEverBright/MyEverBrightBanner/AppGraphic'),
);

interface BannerProps {
  isVisible: boolean;
  onClose: () => void;
  onOpenModal: () => void;
}

export function MyEverBrightBanner({ isVisible, onClose, onOpenModal }: BannerProps) {
  const { t } = useTranslation('translation', { keyPrefix: 'onboarding.myeverbrightBanner' });
  const theme = useTheme();
  const screenSize = useBreakpoints();
  const deviceType = useMemo(guessDeviceType, []);

  const handleButtonClick = useCallback(() => {
    if (deviceType === DeviceType.WEB) {
      onOpenModal();
    }
  }, [deviceType, onOpenModal]);

  /** Only display banner if:
   * - isVisible is true
   * - the user hasn't already seen the banner this session
   * - the app isn't running on mobile (there's a dedicated `MobileAppBanner` with
   * slightly different styling and behavior for that) */
  const showBanner = useMemo(() => {
    if (!isVisible || isMobile) return false;
    const perSessionBannersRaw = localStorage.getItem(perSessionBannersKey);
    const perSessionBanners = perSessionBannersRaw ? JSON.parse(perSessionBannersRaw) : null;
    if (perSessionBanners) {
      const hasViewedBanner = perSessionBanners[perSessionMyEverBrightBannerKey];
      if (hasViewedBanner) return false;
    }
    localStorage.setItem(
      perSessionBannersKey,
      JSON.stringify({ ...perSessionBanners, [perSessionMyEverBrightBannerKey]: true }),
    );
    return true;
  }, [isVisible]);

  const appButton = useMemo(() => {
    const button = (
      <ButtonStyledWrapper>
        <Button
          styleVariant="secondary"
          label={screenSize === 'sm' || screenSize === 'md' ? t('install') : t('getApp')}
          margin="0 0 0 8px"
          onClick={handleButtonClick}
        />
      </ButtonStyledWrapper>
    );
    return deviceType === DeviceType.WEB ? (
      button
    ) : (
      <a
        target="_blank"
        href={
          deviceType === DeviceType.MOBILE_ANDROID
            ? LINKS.MOBILE_APP_STOREFRONTS.google
            : LINKS.MOBILE_APP_STOREFRONTS.apple
        }
        rel="noreferrer"
        aria-label="get app button"
      >
        {button}
      </a>
    );
  }, [deviceType, handleButtonClick, screenSize, t]);

  if (!showBanner) return null;

  return (
    <BannerWrapper>
      <BannerLeftContent>
        <CloseBannerButton onClick={onClose} onKeyUp={(e) => handleFocusedKeyPress(e, onClose)}>
          <CloseIcon color={theme.colors.iconIconPrimarySecondary1100} dimensions="16" />
        </CloseBannerButton>
        <Suspense fallback={<LoadingSpinner />}>
          <AppGraphic />
        </Suspense>
        <BannerTextContent>
          <Text as="p" $fontWeight={700}>
            {t('title')}
          </Text>
          <Text as="sub" $fontWeight={400}>
            {t('body')}
          </Text>
        </BannerTextContent>
      </BannerLeftContent>
      {appButton}
    </BannerWrapper>
  );
}

export default MyEverBrightBanner;
